// "Read it for me": Claude reads photographed maintenance logbook pages and
// returns the entries written on them. Assistive only — the owner reviews and
// edits every entry before it is saved, and nothing touches the maintenance
// tracker unless the owner ticks the box for that entry.
//
// Shared with Solari (flightline-app/src/lib/logbookRead.ts) — keep in step.

import { addDoc, collection, deleteDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';

export type ReadBook = 'airframe' | 'engine' | 'propeller' | 'avionics' | 'other';
export type ReadKind =
  | 'annual'
  | '100_hour'
  | 'oil_change'
  | 'transponder_pitot_static'
  | 'elt'
  | 'ad_compliance'
  | 'repair'
  | 'alteration'
  | 'other';

export interface ReadEntry {
  book: ReadBook;
  date: string; // YYYY-MM-DD or ''
  title: string;
  kind: ReadKind;
  tach: number | null;
  hobbs: number | null;
  total_time: number | null;
  since_overhaul: number | null;
  work_summary: string;
  ad_numbers: string[];
  mechanic: string;
  confidence: 'high' | 'medium' | 'low';
  check_note: string;
}

export type ReadResult =
  | { ok: true; usable: boolean; retake_hint: string; entries: ReadEntry[]; skipped: string[] }
  | { ok: false; reason: string; skipped: string[] };

/** Pages per read — mirrors MAX_PAGES in functions/logbookReader.js. */
export const MAX_READ_PAGES = 12;

// The portal reads through a Firestore trigger, not a callable: this project's
// org policy blocks the public invoker a callable needs. Drop a request doc,
// wait for the trigger to fill in the result. (Solari's copy uses a callable.)
export async function readLogbookPages(input: {
  orgId?: string;
  aircraftId: string;
  paths: string[];
}): Promise<ReadResult> {
  if (!db || !auth?.currentUser) throw new Error('offline');
  const ref = await addDoc(collection(db, 'logbookReads'), {
    ownerUid: auth.currentUser.uid,
    aircraftId: input.aircraftId,
    paths: input.paths,
    status: 'pending',
    createdAt: serverTimestamp(),
  });
  return new Promise<ReadResult>((resolve, reject) => {
    const timer = setTimeout(() => {
      unsub();
      reject(new Error('timeout'));
    }, 300_000);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const d = snap.data();
        if (!d || d.status === 'pending') return;
        clearTimeout(timer);
        unsub();
        deleteDoc(ref).catch(() => {});
        if (d.status === 'done' && d.result) resolve(d.result as ReadResult);
        else reject(new Error(d.error || 'read failed'));
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });
}

export function readFailureMessage(reason: string): string {
  if (reason === 'no_readable_pages')
    return 'Those files can’t be read — photos (JPEG/PNG) and PDFs only, under about 4 MB a photo.';
  if (reason === 'declined') return 'The reader couldn’t process these pages. Fill the entry in by hand.';
  return 'The reader couldn’t make sense of these pages. Try a straighter, brighter photo.';
}

// ── Phone photos → upload-sized JPEGs ───────────────────────────────────────
// Phone photos arrive as 4–12 MB HEIC/JPEG. Long edge 2400 px still reads a
// mechanic's handwriting cleanly, uploads over a weak ramp signal, and stays
// under the reader's per-image limit. PDFs and anything a browser can't decode
// pass through untouched.
const MAX_EDGE = 2400;

export async function shrinkPhoto(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  try {
    const url = URL.createObjectURL(file);
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('unreadable'));
      i.src = url;
    }).finally(() => URL.revokeObjectURL(url));
    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    if (scale === 1 && file.type === 'image/jpeg' && file.size < 2_500_000) return file;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * scale);
    canvas.height = Math.round(img.naturalHeight * scale);
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.86));
    if (!blob) return file;
    const name = file.name.replace(/\.[A-Za-z0-9]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

// ── Entry → logbook fields ──────────────────────────────────────────────────

const fmt = (n: number) => n.toFixed(1);

/** The notes line saved with an entry: the work, the extra meters, ADs, signer. */
export function entryNotes(e: ReadEntry): string {
  const parts: string[] = [];
  if (e.work_summary) parts.push(e.work_summary);
  const meters: string[] = [];
  if (e.total_time != null) meters.push(`TT ${fmt(e.total_time)}`);
  if (e.since_overhaul != null) meters.push(`SMOH ${fmt(e.since_overhaul)}`);
  if (meters.length) parts.push(meters.join(' · '));
  if (e.ad_numbers.length) parts.push(`ADs: ${e.ad_numbers.join(', ')}`);
  if (e.mechanic) parts.push(`Signed: ${e.mechanic}`);
  return parts.join('\n');
}

// ── Entry → maintenance tracker ─────────────────────────────────────────────

/** Which tracker item an entry of this kind completes, by the item's label. */
const KIND_LABEL: Partial<Record<ReadKind, RegExp>> = {
  annual: /annual/i,
  '100_hour': /100[\s-]*(hour|hr)/i,
  oil_change: /oil/i,
  transponder_pitot_static: /transponder|pitot|static|91\.41[13]/i,
  elt: /\belt\b/i,
};

interface TrackerItem {
  itemId: string;
  label: string;
  track: 'date' | 'hours' | 'both';
  intervalDays?: number;
  intervalHours?: number;
  lastDoneDate?: string;
}

/** The tracker item this entry would mark done, if exactly one matches. */
export function matchTrackerItem<T extends TrackerItem>(e: ReadEntry, items: T[]): T | null {
  const re = KIND_LABEL[e.kind];
  if (!re || !e.date) return null;
  // The engine and prop books each record their own annual too; the tracker's
  // single Annual item follows the airframe sign-off.
  if ((e.kind === 'annual' || e.kind === '100_hour') && e.book !== 'airframe') return null;
  const hits = items.filter((i) => re.test(i.label));
  if (hits.length !== 1) return null;
  // Never roll an item backwards: an older page doesn't undo a newer sign-off.
  if (hits[0].lastDoneDate && hits[0].lastDoneDate >= e.date) return null;
  return hits[0];
}

const addDays = (iso: string, days: number) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * The same patch "Mark complete" writes, but dated and metered from the page
 * instead of today. Hours are only rolled when the entry carries a tach.
 */
export function trackerPatch(e: ReadEntry, item: TrackerItem) {
  const patch: {
    lastDoneDate: string;
    lastDoneHobbs?: number;
    lastAlertLevel: number;
    dueDate?: string;
    dueHobbs?: number;
  } = { lastDoneDate: e.date, lastAlertLevel: 0 };
  if (e.tach != null) patch.lastDoneHobbs = e.tach;
  if (item.intervalDays && (item.track === 'date' || item.track === 'both'))
    patch.dueDate = addDays(e.date, item.intervalDays);
  if (item.intervalHours && e.tach != null && (item.track === 'hours' || item.track === 'both'))
    patch.dueHobbs = +(e.tach + item.intervalHours).toFixed(1);
  return patch;
}

// ── Demo ────────────────────────────────────────────────────────────────────
// The demo has no account to call the reader with, so it shows a sample
// reading. Every number here is invented — never a real aircraft's records.
export const DEMO_READ: ReadResult = {
  ok: true,
  usable: true,
  retake_hint: '',
  skipped: [],
  entries: [
    {
      book: 'airframe',
      date: '2026-03-14',
      title: 'Annual inspection',
      kind: 'annual',
      tach: 2207.4,
      hobbs: null,
      total_time: 4312.6,
      since_overhaul: null,
      work_summary:
        'Annual inspection per Part 43 Appendix D. Replaced induction air filter and ELT battery; ELT tested per 91.207(d).',
      ad_numbers: ['2011-10-09'],
      mechanic: 'A&P/IA',
      confidence: 'high',
      check_note: '',
    },
    {
      book: 'engine',
      date: '2026-03-14',
      title: 'Engine annual inspection',
      kind: 'annual',
      tach: 2270.4,
      hobbs: null,
      total_time: null,
      since_overhaul: 684.2,
      work_summary: 'Annual inspection per Part 43 Appendix D. Oil and filter changed, spark plugs cleaned, gapped and rotated.',
      ad_numbers: [],
      mechanic: 'A&P/IA',
      confidence: 'medium',
      check_note: 'Tach reads 2270.4 here but 2207.4 on the airframe entry the same day — check it against the page.',
    },
    {
      book: 'propeller',
      date: '2026-03-14',
      title: 'Propeller annual inspection',
      kind: 'annual',
      tach: 2207.4,
      hobbs: null,
      total_time: null,
      since_overhaul: null,
      work_summary: 'Annual inspection per Part 43 Appendix D. Spinner and bulkhead inspected, blades dressed.',
      ad_numbers: [],
      mechanic: 'A&P/IA',
      confidence: 'high',
      check_note: '',
    },
  ],
};
